import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, FileText, Download, Mail, MessageCircle, Printer, Clock, CheckCircle, XCircle, Send } from 'lucide-react';
import { getErrorMessage } from '../utils/errorHelpers';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MyOpinionsPage = () => {
  const navigate = useNavigate();
  const [opinions, setOpinions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [selectedOpinion, setSelectedOpinion] = useState(null);
  const [emailForm, setEmailForm] = useState({
    recipientEmail: '',
    subject: '',
    message: ''
  });
  const [sendingEmail, setSendingEmail] = useState(false);

  useEffect(() => {
    fetchMyOpinions();
  }, []);

  const fetchMyOpinions = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/opinions/my-opinions`);
      setOpinions(response.data.opinions || []);
    } catch (error) {
      console.error('Error fetching opinions:', error);
      alert('Failed to fetch opinions');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'approved':
        return 'bg-green-100 text-green-800';
      case 'rejected':
        return 'bg-red-100 text-red-800';
      case 'pending_review':
        return 'bg-orange-100 text-orange-800';
      case 'draft':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-blue-100 text-blue-800';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'approved':
        return <CheckCircle className="w-4 h-4" />;
      case 'rejected':
        return <XCircle className="w-4 h-4" />;
      case 'pending_review':
        return <Clock className="w-4 h-4" />;
      default:
        return <FileText className="w-4 h-4" />;
    }
  };

  const handleDownload = async (opinionId, format = 'docx') => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/download/${opinionId}/${format}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const error = await response.json();
        alert(error.detail || 'Failed to download document');
        return;
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `legal_opinion_${opinionId.substring(0, 8)}.${format}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Error downloading document:', error);
      alert('Failed to download document');
    }
  };

  const handlePrint = async (opinionId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/download/${opinionId}/docx`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        alert('Failed to load document for printing');
        return;
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      
      // Open in new window and trigger print
      const printWindow = window.open(url, '_blank');
      if (printWindow) {
        printWindow.onload = () => {
          printWindow.print();
        };
      }
    } catch (error) {
      console.error('Error printing document:', error);
      alert('Failed to print document');
    }
  };

  const handleEmailQuick = (opinion) => {
    const subject = `Legal Opinion - ${opinion.bank_format} - ${opinion.id.substring(0, 8)}`;
    const body = `Please find the legal opinion document attached.\n\nOpinion ID: ${opinion.id}\nBank Format: ${opinion.bank_format}\nStatus: ${opinion.status}\nGenerated: ${new Date(opinion.generated_at).toLocaleDateString()}`;
    
    window.location.href = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };

  const handleEmailWithForm = (opinion) => {
    setSelectedOpinion(opinion);
    setEmailForm({
      recipientEmail: '',
      subject: `Legal Opinion - ${opinion.bank_format} - ${opinion.id.substring(0, 8)}`,
      message: `Please find the legal opinion document.\n\nOpinion ID: ${opinion.id}\nBank Format: ${opinion.bank_format}\nStatus: ${opinion.status}`
    });
    setEmailModalOpen(true);
  };

  const handleSendEmail = async () => {
    if (!emailForm.recipientEmail.trim()) {
      alert('Please enter recipient email');
      return;
    }

    try {
      setSendingEmail(true);
      await axios.post(`${API}/opinions/${selectedOpinion.id}/send-email`, {
        recipient_email: emailForm.recipientEmail,
        subject: emailForm.subject,
        message: emailForm.message
      });

      alert('Email sent successfully!');
      setEmailModalOpen(false);
      setEmailForm({ recipientEmail: '', subject: '', message: '' });
    } catch (error) {
      console.error('Error sending email:', error);
      const errorMsg = getErrorMessage(error, 'Failed to send email');
      alert(errorMsg);
    } finally {
      setSendingEmail(false);
    }
  };

  const handleWhatsApp = async (opinion) => {
    try {
      // Generate shareable link
      const response = await axios.post(`${API}/opinions/${opinion.id}/generate-share-link`);
      
      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to generate share link');
      }
      
      const shareUrl = response.data.share_url;
      
      // Share opinion information with shareable download link
      const message = `Legal Opinion Document 📄\n\n` +
        `Opinion ID: ${opinion.id.substring(0, 8)}\n` +
        `Bank Format: ${opinion.bank_format}\n` +
        `Status: ${opinion.status}\n` +
        `Generated: ${new Date(opinion.generated_at).toLocaleDateString()}\n\n` +
        `✅ Document has been approved and is ready for download.\n\n` +
        `Download here (valid for 24 hours, max 10 downloads):\n${shareUrl}`;
      
      const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(message)}`;
      window.open(whatsappUrl, '_blank');
    } catch (error) {
      console.error('Error generating share link:', error);
      const errorMsg = getErrorMessage(error, 'Failed to generate shareable link');
      alert(`Error: ${errorMsg}`);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">My Opinions</h1>
            <p className="text-gray-600 mt-1">View and manage your legal opinions</p>
          </div>
          <button
            onClick={() => navigate('/dashboard')}
            className="flex items-center space-x-2 px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Back to Dashboard</span>
          </button>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading opinions...</p>
          </div>
        ) : opinions.length === 0 ? (
          <div className="bg-white rounded-lg shadow p-12 text-center">
            <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h2 className="text-2xl font-semibold text-gray-800 mb-2">No Opinions Yet</h2>
            <p className="text-gray-600 mb-6">You haven't created any legal opinions yet.</p>
            <button
              onClick={() => navigate('/dashboard')}
              className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Create New Opinion
            </button>
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Opinion ID</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Bank Format</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Generated</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {opinions.map((opinion) => (
                  <tr key={opinion.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 text-sm font-mono">{opinion.id.substring(0, 8)}...</td>
                    <td className="px-6 py-4 text-sm">
                      <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs font-medium">
                        {opinion.bank_format}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">
                      {formatDate(opinion.generated_at)}
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-3 py-1 rounded-full text-xs font-medium flex items-center space-x-1 w-fit ${getStatusColor(opinion.status)}`}>
                        {getStatusIcon(opinion.status)}
                        <span className="capitalize">{opinion.status?.replace('_', ' ')}</span>
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center space-x-2">
                        {/* Download Button - Always Available */}
                        <button
                          onClick={() => handleDownload(opinion.id, 'docx')}
                          className="p-2 text-blue-600 hover:bg-blue-50 rounded"
                          title="Download DOCX"
                        >
                          <Download className="w-4 h-4" />
                        </button>

                        {/* Actions for Approved Opinions Only */}
                        {opinion.status === 'approved' && (
                          <>
                            {/* Print Button */}
                            <button
                              onClick={() => handlePrint(opinion.id)}
                              className="p-2 text-gray-600 hover:bg-gray-50 rounded"
                              title="Print Document"
                            >
                              <Printer className="w-4 h-4" />
                            </button>

                            {/* Email Dropdown */}
                            <div className="relative group">
                              <button
                                className="p-2 text-green-600 hover:bg-green-50 rounded"
                                title="Email Document"
                              >
                                <Mail className="w-4 h-4" />
                              </button>
                              <div className="absolute right-0 mt-1 w-48 bg-white rounded-lg shadow-lg border border-gray-200 hidden group-hover:block z-10">
                                <button
                                  onClick={() => handleEmailQuick(opinion)}
                                  className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-t-lg"
                                >
                                  Quick Email (mailto)
                                </button>
                                <button
                                  onClick={() => handleEmailWithForm(opinion)}
                                  className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-b-lg"
                                >
                                  Send via Server
                                </button>
                              </div>
                            </div>

                            {/* WhatsApp Button */}
                            <button
                              onClick={() => handleWhatsApp(opinion)}
                              className="p-2 text-green-600 hover:bg-green-50 rounded"
                              title="Share on WhatsApp"
                            >
                              <MessageCircle className="w-4 h-4" />
                            </button>
                          </>
                        )}

                        {/* Actions for Rejected Opinions - Correction Workflow */}
                        {opinion.status === 'rejected' && (
                          <>
                            <button
                              onClick={() => navigate(`/download/${opinion.id}`)}
                              className="px-3 py-1 bg-orange-500 text-white rounded hover:bg-orange-600 text-xs"
                              title="View rejection comments and resubmit"
                            >
                              View & Resubmit
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Email Modal */}
        {emailModalOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg shadow-2xl max-w-md w-full p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold text-gray-900">Send Email</h2>
                <button
                  onClick={() => setEmailModalOpen(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ✕
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Recipient Email *
                  </label>
                  <input
                    type="email"
                    value={emailForm.recipientEmail}
                    onChange={(e) => setEmailForm({ ...emailForm, recipientEmail: e.target.value })}
                    placeholder="recipient@example.com"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Subject
                  </label>
                  <input
                    type="text"
                    value={emailForm.subject}
                    onChange={(e) => setEmailForm({ ...emailForm, subject: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Message
                  </label>
                  <textarea
                    value={emailForm.message}
                    onChange={(e) => setEmailForm({ ...emailForm, message: e.target.value })}
                    rows="4"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>

              <div className="flex space-x-3 mt-6">
                <button
                  onClick={handleSendEmail}
                  disabled={sendingEmail || !emailForm.recipientEmail.trim()}
                  className="flex-1 flex items-center justify-center space-x-2 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
                >
                  <Send className="w-4 h-4" />
                  <span>{sendingEmail ? 'Sending...' : 'Send Email'}</span>
                </button>
                <button
                  onClick={() => setEmailModalOpen(false)}
                  className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MyOpinionsPage;
